import { AuditTrail } from '@card-software/nexus-models';
import { LotItem } from '../inventory/lot-item';
import { Invoice, S3Object } from '../invoice/invoice';

export interface Shipment extends AuditTrail {
  id?: string;
  trackingNumber: string;
  lots?: LotItem[];
  invoiceReferences: string[];
  deliveryNoteReferences?: string[];
  invoices?: Invoice[];
  deliveryNotes?: DeliveryNote[];
  estimatedArrivalDate: Date;
  actualArrivalDate: Date;
  shipmentNumber?: string;
  purchaseOrderReference?: string;
  purchaseOrderNumber: string;
  shipmentDate: Date;
  status: number;
  exporter: Exporter;
  ducaInformation: DucaInformation;
  transport: Transport;
  transporter: Transporter;
  totalValues: TotalValues;
  files?: S3Object[];
  duca?: S3Object;
  packingList?: S3Object[];
  entities?: EntityKeyByReference[];
  type: number;
  clientReference?: string;
  insuranceCost: number;
  shippingCost: number;
}

export interface Exporter {
  nameOrSocialReason?: string | null;
  fiscalAddress?: string | null;
}

export interface DucaInformation {
  uniqueReference?: string | null;
  ducaNumber?: string | null;
  customsOfficeInitial?: string | null;
  customsOfficeDeparture?: string | null;
  customsOfficeDestination?: string | null;
}
export interface Transport {
  code?: string | null;
  name?: string | null;
}

export interface Transporter {
  driverFirstAndLastName?: string | null;
  transportUnitId?: string | null;
  trailerIds?: string[] | null;
  marchamosNumbers?: string[] | null;
}

export interface TotalValues {
  totalValueInUSD?: number | null;
  exchangeRate?: number | null;
  localCurrency?: string | null;
}

export interface Merchandise {
  units?: number | null;
  unitType?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  uom?: number | null;
  description?: string | null;
  customsCode?: string | null;
  countryCode?: string | null;
}

export interface File {
  name: string;
  presignedUrl: string;
}

export interface InvoiceLotAction {
  invoiceReference: string;
  purchaseOrderReference: string;
  newLots: LotItem[];
  lotIdsToDelete: string[];
}

export interface DeliveryNoteLotAction {
  deliveryNoteReference: string;
  workOrderReference: string;
  newLots: LotItem[];
  lotIdsToDelete: string[];
}

export interface ConsignmentBatchCommand {
  shipmentReference: string;
  lotItems: LotItem[];
}

export interface DeliveryNoteBatchCommand {
  shipmentReference: string;
  deliveryNoteLotActions: DeliveryNoteLotAction[];
}

export enum ShipmentStatus {
  Draft = 0,
  InRoute = 1,
  Arrived = 2,
  InCustoms = 3,
  ReadyToUnload = 4,
  Unloading = 5,
  Completed = 6,
  Cancelled = 7,
}

export interface ShipmentStatusCommand {
  newStatus: number;
}

export interface ShipmentSearch {
  trackingNumber?: string;
  actualArrivalDate?: Date;
  invoiceReferences?: string[];
}

// form groups
// shipment-form-groups.ts
import { FormControl } from '@angular/forms';
import { DeliveryNote } from '../delivery-note/delivery-note';
import { EntityKeyByReference } from '@card-software/nexus-utilities';

// MERCHANDISE (used in FormArray)
export interface MerchandiseFormGroup {
  units: FormControl<number | null>;
  unitType: FormControl<string | null>;
  netWeight: FormControl<number | null>;
  grossWeight: FormControl<number | null>;
  uom: FormControl<number | null>;
  description: FormControl<string | null>;
  customsCode: FormControl<string | null>;
  countryCode: FormControl<string | null>;
}

// EXPORTER
export interface ExporterFormGroup {
  nameOrSocialReason: FormControl<string | null>;
  fiscalAddress: FormControl<string | null>;
}

// DUCA INFO
export interface DucaInformationFormGroup {
  uniqueReference: FormControl<string | null>;
  ducaNumber: FormControl<string | null>;
  customsOfficeInitial: FormControl<string | null>;
  customsOfficeDeparture: FormControl<string | null>;
  customsOfficeDestination: FormControl<string | null>;
}

// TRANSPORT
export interface TransportFormGroup {
  code: FormControl<string | null>;
  name: FormControl<string | null>;
}

// TRANSPORTER
export interface TransporterFormGroup {
  driverFirstAndLastName: FormControl<string | null>;
  transportUnitId: FormControl<string | null>;
  trailerIds: FormControl<string[] | null>;
  marchamosNumbers: FormControl<string[] | null>;
}

// TOTAL VALUES
export interface TotalValuesFormGroup {
  totalValueInUSD: FormControl<number | null>;
  exchangeRate: FormControl<number | null>;
  localCurrency: FormControl<string | null>;
}

// SUPPORTING DOCUMENT IDS
export interface SupportingDocumentIdsFormGroup {
  supportingDocumentIds: FormControl<string[] | null>;
}
